import axios, { AxiosInstance, AxiosResponse, AxiosError } from "axios";
import Env from "../env/Env";
import { defaultRequestHeaders } from "../consts/shared";
import { getInternalToken, getOriginalMyakoToken, setInternalToken, clearInternalToken, getRedirectURL } from "./tokenUtils";
import authAPIsService from "../hooks/authAPIs";
import { getActiveTenantId } from "../store/tenantStore";

// Token refresh queue to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];

const processQueue = (token: string) => {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
};

const subscribeTokenRefresh = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

// Create a shared axios instance
const createAxiosInstance = (baseURLOrPath: string): AxiosInstance => {
  const instance = axios.create({
    baseURL: baseURLOrPath,
    timeout: 300000,
    headers: defaultRequestHeaders,
  });

  // Request interceptor to add token, tenant headers, and dynamic API base
  instance.interceptors.request.use((config) => {
    const activeTenant = getActiveTenantId();
    const token = getInternalToken(activeTenant);
    const apiBase = Env.API_BASE;

    // Dynamically update baseURL if it contains the API host or relative path
    if (config.baseURL) {
      if (config.baseURL.startsWith('http://') || config.baseURL.startsWith('https://')) {
        try {
          const parsed = new URL(config.baseURL);
          config.baseURL = `${apiBase}${parsed.pathname}`;
        } catch (e) {
          // If URL parsing fails, retain current baseURL
        }
      } else {
        config.baseURL = `${apiBase}${config.baseURL.startsWith('/') ? config.baseURL : '/' + config.baseURL}`;
      }
    }

    config.headers = config.headers || {};
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (activeTenant) {
      config.headers['x-tenant-id'] = activeTenant;
      config.headers['client'] = activeTenant;
    }

    return config;
  });

  // Response interceptor to handle token refresh
  instance.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as any;

      // If the error is 403 and we haven't already tried to refresh
      if (error.response?.status === 403 && !originalRequest._retry) {
        originalRequest._retry = true;

        // If already refreshing, subscribe to the refresh completion
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            subscribeTokenRefresh((token: string) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(instance(originalRequest));
            });
          });
        }

        isRefreshing = true;

        try {
          // Get the stored external token for current tenant
          const activeTenant = getActiveTenantId();
          const externalToken = getOriginalMyakoToken(activeTenant);
          
          if (externalToken) {
            console.log('Token expired, attempting silent refresh...');
            
            // Attempt to refresh the internal token using the external token
            const refreshData = await authAPIsService.validateMyakoToken(externalToken, activeTenant);
            
            const redirectURL = getRedirectURL(activeTenant);
            
            // Update the stored token
            setInternalToken(
              refreshData.internalToken,
              refreshData.role,
              refreshData.organisationId,
              externalToken,
              redirectURL || undefined,
              activeTenant
            );

            console.log('Token refreshed successfully, processing queue...');

            // Process all queued requests with the new token
            processQueue(refreshData.internalToken);

            // Retry the original request with the new token
            originalRequest.headers.Authorization = `Bearer ${refreshData.internalToken}`;
            return instance(originalRequest);
          } else {
            // No external token available, clear internal token and redirect to login
            console.log('No external token available for refresh, redirecting to login...');
            const activeTenant = getActiveTenantId();
            const redirectURL = getRedirectURL(activeTenant);
            clearInternalToken(activeTenant);
            window.location.href = redirectURL || '/';
            return Promise.reject(error);
          }
        } catch (refreshError) {
          // Token refresh failed, clear tokens and redirect to login
          console.error('Token refresh failed:', refreshError);
          const activeTenant = getActiveTenantId();
          const redirectURL = getRedirectURL(activeTenant);
          clearInternalToken(activeTenant);
          window.location.href = redirectURL || '/';
          return Promise.reject(error);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

export default createAxiosInstance;